/**
 * The action the player is composing — the one field of state the
 * action bar does not own.
 *
 * It exists because *"clicking `water_bottle` writes 'drink from the water bottle' into the
 * action box"* is a sentence about two components that have no path to each other: the
 * character panel is a drawer over the feed, the action bar is sticky at its foot, and their
 * nearest common ancestor is the route. Threading a `draft` prop and a setter down through
 * `CharacterPanel` → `PanelBody` → `Inventory` → a row would put four components in the
 * business of an input's value, which is how the panel ends up re-rendering on every
 * keystroke.
 *
 * So the box's value lives here and the panel calls `suggestAction`. This is deliberately the
 * *smallest* store that makes the loop work — it holds a string and a counter, it knows
 * nothing about turns, and `turn/store.ts` neither reads nor writes it.
 *
 * **`writes` is the whole trick.** A suggestion has to focus the box, and focusing on a value
 * change would fight the player every time they type. So an external write bumps a counter,
 * the action bar keys its focus effect on that counter, and typing — which does not bump it —
 * moves nothing. It also makes *"suggest the same item twice"* work, which a value-keyed
 * effect gets wrong by construction: the second click changes no string at all.
 */
import { create } from 'zustand';

interface DraftStore {
  text: string;
  /** Incremented only by `suggest`. The action bar focuses when it moves. */
  writes: number;
  /** From the input itself. Never focuses, never counts. */
  type(text: string): void;
  /** From somewhere else in the UI: replace the box and take the caret there. */
  suggest(text: string): void;
  clear(): void;
}

export const useDraftStore = create<DraftStore>()((set) => ({
  text: '',
  writes: 0,
  type: (text) => set({ text }),
  suggest: (text) => set((state) => ({ text, writes: state.writes + 1 })),
  clear: () => set({ text: '' }),
}));

/**
 * Callable from an event handler anywhere — the inventory row, the injury row, and whatever
 * `TODO.md`'s codex links turn out to be.
 *
 * It does not submit. The brief is explicit that the player *edits or submits*: the phrasing is
 * a starting point written by a component that knows what was clicked, not an action the
 * player chose, and a click that spent $0.13 on the DM's guess at their intent would be a
 * worse affordance than no affordance.
 */
export function suggestAction(text: string): void {
  useDraftStore.getState().suggest(text);
}
